/**
 * The live assistant's conversation in this tab — one store, outside React.
 *
 * The chat lives in the "Ask Optivo" window, which the rail's provider mounts
 * once; but a window can be minimised, closed, hidden while another property
 * is open, or lost to a render fault, and the rail itself can be replaced by
 * what stands in for it. An answer streaming in must outlive all of that, so
 * the conversation, the stream reading it and what is being typed live here,
 * in the module-level style of `priceGenerationTracker.ts`, read through
 * `useSyncExternalStore` (`useAssistantChat`). The server keeps the
 * conversation too, so a reload reads it back (`ensureAssistantLoaded`).
 *
 * It holds one property's conversation at a time: the one the window is for.
 * Another property drops it — the stream stopped, the draft and the handover
 * note cleared — and the next look reads that property's conversation from
 * the server. A stream is stopped by Stop, by a new conversation and by a
 * change of property; never by minimising, closing or changing the page.
 *
 * Text arrives a few characters at a time; it is gathered and drawn once an
 * animation frame rather than once per piece. When an answer ends — done,
 * broken off with an error, or cut off — `answerSignal` ticks, which is what
 * the rail flashes on while the window is not open, and the conversation is
 * read back from the server. What streamed is the model's own words; what the
 * server stored is scrubbed of outside addresses and the market-data vendors'
 * names, so `announcement`, the window's one screen-reader announcement of the
 * answer, waits for the read-back and carries the answer as stored. A
 * read-back that fails, or does not bring that answer within a few seconds,
 * announces only that an answer arrived: the streamed words are never
 * announced. An answer the operator stopped ticks neither: they were there.
 *
 * Nothing here logs or throws what was asked or answered.
 */

import { useSyncExternalStore } from "react";
import { ActiveConnectionChangedError } from "./apiClient";
import {
    AssistantRequestError,
    closeAssistantConversation,
    fetchAssistantState,
    handOverAssistantConversation,
    sendAssistantFeedback,
    streamAssistantAnswer,
    ASSISTANT_DEFAULT_MAX_MESSAGE_CHARS,
    type AssistantAnswerMessage,
    type AssistantFeedback,
    type AssistantHandover,
    type AssistantLimits,
    type AssistantLocale,
    type AssistantMessage,
    type AssistantPageContext,
    type AssistantState,
    type AssistantStreamEvent,
    type AssistantUnavailableReason,
    type AssistantUserMessage,
} from "./assistantApi";
import { isAbortError } from "./jobPolling";

/** An answer as the window holds it: what the server stored, and what only this tab knows about it. */
export interface ChatAnswer extends AssistantAnswerMessage {
    /** Stopped by the operator in this window. */
    stopped?: boolean;
    /** The code of the error the answer broke off with. */
    errorCode?: string | null;
}

export type ChatMessage = AssistantUserMessage | ChatAnswer;

export interface ChatToolStatus {
    name: string;
    subject: string | null;
}

export type ChatProblem =
    | { kind: "refusal"; code: string | null; limit: number | null }
    | { kind: "stream"; code: string }
    | { kind: "connectionChanged" }
    | { kind: "network" };

export interface ChatHandoverState {
    /** The confirmation is on screen. */
    open: boolean;
    note: string;
    sending: boolean;
    problem: { code: string | null } | null;
    /** Handed over from this window, just now. */
    result: { requestId: number; caseNumber: string } | null;
}

export interface AssistantChatSnapshot {
    connectionId: number | null;
    loading: boolean;
    loaded: boolean;
    loadFailed: boolean;
    available: boolean;
    unavailableReason: AssistantUnavailableReason | null;
    conversationId: string | null;
    status: "open" | "handed_over" | null;
    handedOver: { requestId: number; caseNumber: string } | null;
    messages: ChatMessage[];
    streaming: boolean;
    toolStatus: ChatToolStatus | null;
    problem: ChatProblem | null;
    limits: AssistantLimits | null;
    retentionDays: number | null;
    activeStrategyId: string | null;
    /** Ticks each time an answer ends here, by itself rather than by Stop. */
    answerSignal: number;
    /**
     * The last answer that ended here, as the server stored it — for one
     * announcement. `answer` is null where the stored answer could not be read
     * in time: only that an answer arrived is said.
     */
    announcement: { id: number; answer: ChatAnswer | null } | null;
    draft: string;
    handover: ChatHandoverState;
}

export type ChatSendResult = { ok: true } | { ok: false; error: unknown };
export type ChatHandoverResult = { ok: true; handover: AssistantHandover } | { ok: false; error: unknown };

/** A Stop this recent explains a busy refusal: the server is still writing the stopped answer down. */
const BUSY_RETRY_WINDOW_MS = 5_000;
const BUSY_RETRY_DELAY_MS = 1_000;
/** A stopped answer is read back from the server this long after, once it has written it down. */
const STOPPED_REREAD_MS = 1_500;
/** An answer the server still reports as being written is read again this often, this many times. */
const STILL_WRITING_RECHECK_MS = 3_000;
const STILL_WRITING_RECHECKS = 20;
/** An answer the read-back has not brought this long after it ended is announced without its words. */
const ANNOUNCE_FALLBACK_MS = 3_000;

const CLOSED_HANDOVER: ChatHandoverState = { open: false, note: "", sending: false, problem: null, result: null };

function initialSnapshot(connectionId: number | null, answerSignal: number): AssistantChatSnapshot {
    return {
        connectionId,
        loading: false,
        loaded: false,
        loadFailed: false,
        available: false,
        unavailableReason: null,
        conversationId: null,
        status: null,
        handedOver: null,
        messages: [],
        streaming: false,
        toolStatus: null,
        problem: null,
        limits: null,
        retentionDays: null,
        activeStrategyId: null,
        answerSignal,
        announcement: null,
        draft: "",
        handover: CLOSED_HANDOVER,
    };
}

let snapshot: AssistantChatSnapshot = initialSnapshot(null, 0);
const listeners = new Set<() => void>();

interface Turn {
    controller: AbortController;
    userId: string;
    answerId: string;
    buffered: string;
}

/** The turn streaming now; a turn that is not this one speaks for nobody. */
let currentTurn: Turn | null = null;
let flushHandle: { cancel: () => void } | null = null;
let localIds = 0;
let announcements = 0;
let lastStopAt = 0;
/** Bumped by every reset and every turn: a read started before speaks for nobody. */
let epoch = 0;
let loadingPromise: Promise<void> | null = null;
let recheck: ReturnType<typeof setTimeout> | null = null;
let rechecksLeft = STILL_WRITING_RECHECKS;

/** The answer that ended here and waits for its stored copy to be announced. */
interface AwaitedAnnouncement {
    answerId: string;
    fallback: ReturnType<typeof setTimeout>;
}
let awaited: AwaitedAnnouncement | null = null;

function emit(): void {
    for (const listener of listeners) listener();
}

function update(patch: Partial<AssistantChatSnapshot>): void {
    snapshot = { ...snapshot, ...patch };
    emit();
}

function updateAnswer(id: string, patch: Partial<ChatAnswer>): void {
    update({
        messages: snapshot.messages.map((message) =>
            message.id === id && message.role === "assistant" ? { ...message, ...patch } : message),
    });
}

function answerById(id: string): ChatAnswer | null {
    const found = snapshot.messages.find((message) => message.id === id);
    return found && found.role === "assistant" ? found : null;
}

// ---------------------------------------------------------------------------
// Announcing an answer, once, as the server stored it
// ---------------------------------------------------------------------------

/** Says the answer waiting — as stored, or (null) only that it arrived — and stops waiting. */
function settleAnnouncement(answer: ChatAnswer | null): void {
    if (!awaited) return;
    clearTimeout(awaited.fallback);
    awaited = null;
    announcements += 1;
    update({ announcement: { id: announcements, answer } });
}

/** An answer that ended here waits for the read-back; one still waiting is said to have arrived. */
function awaitAnnouncement(answerId: string): void {
    settleAnnouncement(null);
    const waiting: AwaitedAnnouncement = {
        answerId,
        fallback: setTimeout(() => {
            if (awaited === waiting) settleAnnouncement(null);
        }, ANNOUNCE_FALLBACK_MS),
    };
    awaited = waiting;
}

/** The conversation the answer belonged to is gone: nothing is said of it. */
function dropAnnouncement(): void {
    if (awaited) clearTimeout(awaited.fallback);
    awaited = null;
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

export function getAssistantChatSnapshot(): AssistantChatSnapshot {
    return snapshot;
}

/** The conversation as the window draws it. */
export function useAssistantChat(): AssistantChatSnapshot {
    return useSyncExternalStore(subscribe, getAssistantChatSnapshot, getAssistantChatSnapshot);
}

// ---------------------------------------------------------------------------
// Drawing text once a frame
// ---------------------------------------------------------------------------

function scheduleFrame(callback: () => void): { cancel: () => void } {
    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
        const handle = window.requestAnimationFrame(callback);
        return { cancel: () => window.cancelAnimationFrame(handle) };
    }
    const handle = setTimeout(callback, 16);
    return { cancel: () => clearTimeout(handle) };
}

function flushText(turn: Turn): void {
    flushHandle?.cancel();
    flushHandle = null;
    if (turn.buffered === "") return;
    const answer = answerById(turn.answerId);
    const text = `${answer?.text ?? ""}${turn.buffered}`;
    turn.buffered = "";
    update({
        toolStatus: null,
        messages: snapshot.messages.map((message) =>
            message.id === turn.answerId && message.role === "assistant" ? { ...message, text } : message),
    });
}

function queueText(turn: Turn, delta: string): void {
    turn.buffered += delta;
    if (flushHandle) return;
    flushHandle = scheduleFrame(() => {
        flushHandle = null;
        if (turn === currentTurn) flushText(turn);
    });
}

// ---------------------------------------------------------------------------
// Reading the conversation
// ---------------------------------------------------------------------------

function clearRecheck(): void {
    if (recheck !== null) clearTimeout(recheck);
    recheck = null;
}

/** What only this tab knows about an answer, carried onto the server's copy of it. */
function mergeMessages(incoming: AssistantMessage[]): ChatMessage[] {
    return incoming.map((message) => {
        if (message.role !== "assistant") return message;
        const local = answerById(message.id);
        if (!local?.stopped && !local?.errorCode) return message;
        return {
            ...message,
            ...(local.stopped ? { stopped: true } : {}),
            ...(local.errorCode ? { errorCode: local.errorCode } : {}),
        };
    });
}

function applyState(state: AssistantState): void {
    const conversation = state.conversation;
    update({
        loading: false,
        loaded: true,
        loadFailed: false,
        available: state.available,
        unavailableReason: state.unavailableReason,
        limits: state.limits,
        retentionDays: state.retentionDays,
        activeStrategyId: state.activeStrategyId,
        conversationId: conversation?.id ?? null,
        status: conversation?.status ?? null,
        handedOver: conversation?.handedOver ?? null,
        messages: conversation ? mergeMessages(conversation.messages) : [],
    });
    // The answer waiting is announced as stored, once the server has written
    // it down; a read that does not hold it never will.
    if (awaited) {
        const stored = answerById(awaited.answerId);
        if (!stored || stored.status !== "streaming") settleAnnouncement(stored);
    }
    // An answer another tab — or this one before a reload — is still writing
    // is read again until it is done.
    const last = snapshot.messages[snapshot.messages.length - 1];
    clearRecheck();
    if (last?.role === "assistant" && last.status === "streaming" && !currentTurn && rechecksLeft > 0) {
        rechecksLeft -= 1;
        const at = epoch;
        recheck = setTimeout(() => {
            recheck = null;
            if (at === epoch && !currentTurn) void refreshAssistantChat();
        }, STILL_WRITING_RECHECK_MS);
    }
}

async function load(): Promise<void> {
    const at = epoch;
    const connectionId = snapshot.connectionId;
    update({ loading: true });
    try {
        const state = await fetchAssistantState();
        if (at === epoch) {
            rechecksLeft = STILL_WRITING_RECHECKS;
            applyState(state);
        }
    } catch {
        if (at === epoch) update({ loadFailed: true });
    } finally {
        // A question asked meanwhile reads the conversation itself; another
        // property's read is that property's to finish.
        if (snapshot.loading && snapshot.connectionId === connectionId) update({ loading: false });
    }
}

/** Reads the conversation again in place, quietly: a failure leaves what is shown. */
export async function refreshAssistantChat(): Promise<void> {
    if (snapshot.connectionId === null || currentTurn) return;
    const at = epoch;
    const waiting = awaited;
    try {
        const state = await fetchAssistantState();
        if (at !== epoch || currentTurn) return;
        applyState(state);
    } catch {
        // What the window shows stands; an answer waiting for this read is
        // said to have arrived, without its words.
        if (waiting && awaited === waiting) settleAnnouncement(null);
    }
}

function reset(connectionId: number | null): void {
    epoch += 1;
    const turn = currentTurn;
    currentTurn = null;
    flushHandle?.cancel();
    flushHandle = null;
    clearRecheck();
    dropAnnouncement();
    loadingPromise = null;
    turn?.controller.abort();
    snapshot = initialSnapshot(connectionId, snapshot.answerSignal);
    emit();
}

/**
 * Says which property the window is for. Another one than before stops the
 * answer streaming for the last, and drops its conversation, draft and note.
 */
export function selectAssistantConnection(connectionId: number): void {
    if (snapshot.connectionId !== connectionId) reset(connectionId);
}

/** Reads the property's conversation, once: what the window does when the chat is shown. */
export function ensureAssistantLoaded(connectionId: number): Promise<void> {
    selectAssistantConnection(connectionId);
    if (snapshot.loaded) return Promise.resolve();
    if (!loadingPromise) {
        loadingPromise = load().finally(() => {
            loadingPromise = null;
        });
    }
    return loadingPromise;
}

/** Drops everything: the window has no property to be for. */
export function forgetAssistantConnection(): void {
    reset(null);
}

// ---------------------------------------------------------------------------
// Asking
// ---------------------------------------------------------------------------

function delay(ms: number, signal: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        if (signal.aborted) {
            reject(new DOMException("Stopped", "AbortError"));
            return;
        }
        const timer = setTimeout(() => {
            signal.removeEventListener("abort", stop);
            resolve();
        }, ms);
        const stop = () => {
            clearTimeout(timer);
            reject(new DOMException("Stopped", "AbortError"));
        };
        signal.addEventListener("abort", stop, { once: true });
    });
}

/**
 * The end of a turn that ended by itself: the stream released, the signal
 * ticked, and the answer waiting to be announced as the read-back brings it.
 */
function finishTurn(turn: Turn, patch: Partial<AssistantChatSnapshot> = {}): void {
    currentTurn = null;
    awaitAnnouncement(turn.answerId);
    update({
        streaming: false,
        toolStatus: null,
        answerSignal: snapshot.answerSignal + 1,
        ...patch,
    });
}

function onEvent(turn: Turn, event: AssistantStreamEvent): void {
    if (turn !== currentTurn) return;
    switch (event.type) {
        case "start": {
            const userId = event.userMessageId || turn.userId;
            const answerId = event.assistantMessageId;
            update({
                conversationId: event.conversationId,
                status: "open",
                handedOver: null,
                activeStrategyId: event.activeStrategyId ?? snapshot.activeStrategyId,
                messages: snapshot.messages.map((message) => {
                    if (message.id === turn.userId) return { ...message, id: userId };
                    if (message.id === turn.answerId) return { ...message, id: answerId };
                    return message;
                }),
            });
            turn.userId = userId;
            turn.answerId = answerId;
            return;
        }
        case "tool":
            if (event.phase === "start") {
                flushText(turn);
                update({ toolStatus: { name: event.name, subject: event.subject } });
            }
            return;
        case "text":
            queueText(turn, event.delta);
            return;
        case "done": {
            flushText(turn);
            const answer = answerById(turn.answerId);
            updateAnswer(turn.answerId, {
                status: event.stopReason === "timeout" ? "interrupted" : "complete",
                stopReason: event.stopReason,
                // A refusal is never shown in part: the window says it cannot help instead.
                text: event.stopReason === "refusal" ? "" : answer?.text ?? "",
            });
            const limits = snapshot.limits;
            finishTurn(turn, limits ? { limits: { ...limits, usedToday: limits.usedToday + 1 } } : {});
            return;
        }
        case "error": {
            flushText(turn);
            const text = answerById(turn.answerId)?.text ?? "";
            updateAnswer(turn.answerId, { status: text.trim() ? "interrupted" : "failed", errorCode: event.code });
            finishTurn(turn, { problem: { kind: "stream", code: event.code } });
            return;
        }
    }
}

function problemOf(err: unknown): ChatProblem {
    if (err instanceof ActiveConnectionChangedError) return { kind: "connectionChanged" };
    if (err instanceof AssistantRequestError) {
        return { kind: "refusal", code: err.code, limit: err.limit };
    }
    return { kind: "network" };
}

/**
 * Asks the question and streams the answer into the transcript. Resolves when
 * the answer has ended, however it ended; a refusal for another property comes
 * back as `error`, for the caller to say in the shell's words.
 */
export async function sendAssistantQuestion(
    text: string,
    page: AssistantPageContext | null,
    locale: AssistantLocale,
): Promise<ChatSendResult> {
    const question = text.trim();
    const connectionId = snapshot.connectionId;
    if (question === "" || snapshot.streaming || connectionId === null || snapshot.status === "handed_over") {
        return { ok: false, error: null };
    }
    epoch += 1;
    // This turn's: a read-back started for it speaks for nobody once another starts.
    const at = epoch;
    clearRecheck();
    localIds += 1;
    const turn: Turn = {
        controller: new AbortController(),
        userId: `local-question-${localIds}`,
        answerId: `local-answer-${localIds}`,
        buffered: "",
    };
    currentTurn = turn;
    const now = new Date().toISOString();
    // A suggestion is sent beside what is typed: the box keeps it.
    const sendsDraft = question === snapshot.draft.trim();
    update({
        messages: [
            ...snapshot.messages,
            { id: turn.userId, role: "user", text: question, createdAt: now },
            {
                id: turn.answerId, role: "assistant", text: "", createdAt: now,
                status: "streaming", stopReason: null, feedback: null, generatedBy: "ai",
            },
        ],
        streaming: true,
        toolStatus: null,
        problem: null,
        draft: sendsDraft ? "" : snapshot.draft,
        handover: CLOSED_HANDOVER,
    });

    const ask = () => streamAssistantAnswer(
        { connectionId, conversationId: snapshot.conversationId, message: question, locale, page },
        (event) => onEvent(turn, event),
        turn.controller.signal,
    );

    try {
        let outcome;
        try {
            outcome = await ask();
        } catch (err) {
            const justStopped = Date.now() - lastStopAt < BUSY_RETRY_WINDOW_MS;
            if (!(err instanceof AssistantRequestError && err.code === "ASSISTANT_BUSY" && justStopped)) throw err;
            await delay(BUSY_RETRY_DELAY_MS, turn.controller.signal);
            if (turn !== currentTurn) return { ok: true };
            outcome = await ask();
        }
        if (turn !== currentTurn) {
            // Done and error ended the turn as they arrived. What streamed is
            // the model's own words; what the window keeps is the answer as
            // the server stored it, scrubbed of outside addresses and the
            // market-data vendors' names.
            if (at === epoch && (outcome === "done" || outcome === "error")) void refreshAssistantChat();
            return { ok: true };
        }
        // A stream cut off without either is read back from the server.
        if (outcome === "interrupted") {
            flushText(turn);
            const answer = answerById(turn.answerId);
            updateAnswer(turn.answerId, { status: answer?.text.trim() ? "interrupted" : "failed" });
            finishTurn(turn);
        }
        if (outcome !== "error") void refreshAssistantChat();
        return { ok: true };
    } catch (err) {
        if (turn !== currentTurn) return { ok: true };
        if (isAbortError(err)) {
            flushText(turn);
            currentTurn = null;
            updateAnswer(turn.answerId, { status: "interrupted", stopped: true });
            update({ streaming: false, toolStatus: null });
            // What the server stored of it — and the conversation, where the
            // answer was stopped before the server named one.
            setTimeout(() => {
                if (at === epoch) void refreshAssistantChat();
            }, STOPPED_REREAD_MS);
            return { ok: true };
        }
        // Refused before anything was answered: the transcript as it was, and
        // the question back in the box to be put right.
        currentTurn = null;
        update({
            messages: snapshot.messages.filter((message) => message.id !== turn.userId && message.id !== turn.answerId),
            streaming: false,
            toolStatus: null,
            draft: snapshot.draft === "" ? question : snapshot.draft,
            problem: problemOf(err),
        });
        return { ok: false, error: err };
    }
}

/** Stops the answer streaming; what arrived of it stays. */
export function stopAssistantAnswer(): void {
    if (!currentTurn) return;
    lastStopAt = Date.now();
    currentTurn.controller.abort();
}

/** A new conversation: the answer stopped, the old one closed on the server, what is typed kept. */
export async function newAssistantConversation(): Promise<void> {
    const { conversationId, connectionId, status } = snapshot;
    epoch += 1;
    const turn = currentTurn;
    currentTurn = null;
    flushHandle?.cancel();
    flushHandle = null;
    clearRecheck();
    dropAnnouncement();
    if (turn) {
        lastStopAt = Date.now();
        turn.controller.abort();
    }
    update({
        conversationId: null,
        status: null,
        handedOver: null,
        messages: [],
        streaming: false,
        toolStatus: null,
        problem: null,
        announcement: null,
        handover: CLOSED_HANDOVER,
    });
    if (conversationId && connectionId !== null && status === "open") {
        try {
            await closeAssistantConversation(conversationId, connectionId);
        } catch {
            // A conversation that could not be closed is only read back after
            // a reload, until the next question starts a new one.
        }
    }
}

export function setAssistantDraft(draft: string): void {
    if (draft !== snapshot.draft) update({ draft });
}

export function clearAssistantProblem(): void {
    if (snapshot.problem) update({ problem: null });
}

// ---------------------------------------------------------------------------
// Handing over, and rating
// ---------------------------------------------------------------------------

export function openAssistantHandover(): void {
    update({ handover: { ...snapshot.handover, open: true, problem: null, result: null } });
}

export function cancelAssistantHandover(): void {
    update({ handover: { ...snapshot.handover, open: false, problem: null } });
}

export function setAssistantHandoverNote(note: string): void {
    update({ handover: { ...snapshot.handover, note } });
}

/** Hands the conversation to support as an ordinary question, with the note. */
export async function handOverAssistantChat(): Promise<ChatHandoverResult> {
    const { conversationId, connectionId } = snapshot;
    if (!conversationId || connectionId === null || snapshot.handover.sending) return { ok: false, error: null };
    const at = epoch;
    const note = snapshot.handover.note.trim();
    update({ handover: { ...snapshot.handover, sending: true, problem: null } });
    try {
        const handover = await handOverAssistantConversation(conversationId, note || null, connectionId);
        if (at !== epoch) return { ok: true, handover };
        const handedOver = { requestId: handover.request.id, caseNumber: handover.caseNumber };
        update({
            status: "handed_over",
            handedOver,
            handover: { ...CLOSED_HANDOVER, result: handedOver },
        });
        return { ok: true, handover };
    } catch (err) {
        if (at === epoch) {
            const code = err instanceof AssistantRequestError
                ? err.code
                : err instanceof ActiveConnectionChangedError ? "ACTIVE_CONNECTION_CHANGED" : null;
            update({ handover: { ...snapshot.handover, sending: false, problem: { code } } });
            if (code === "ASSISTANT_ALREADY_HANDED_OVER") void refreshAssistantChat();
        }
        return { ok: false, error: err };
    }
}

/** Rates an answer at once; the rating is taken back if it was not saved. Resolves whether it was. */
export async function rateAssistantAnswer(messageId: string, value: AssistantFeedback): Promise<boolean> {
    const connectionId = snapshot.connectionId;
    const answer = answerById(messageId);
    if (connectionId === null || !answer) return false;
    const previous = answer.feedback;
    updateAnswer(messageId, { feedback: value });
    try {
        await sendAssistantFeedback(messageId, value, connectionId);
        return true;
    } catch {
        if (answerById(messageId)?.feedback === value) updateAnswer(messageId, { feedback: previous });
        return false;
    }
}

/** The longest question the server takes, as last read. */
export function assistantMaxMessageChars(): number {
    return snapshot.limits?.maxMessageChars ?? ASSISTANT_DEFAULT_MAX_MESSAGE_CHARS;
}

/** One test's conversation must not be the next's. */
export function resetAssistantChatForTests(): void {
    reset(null);
    snapshot = initialSnapshot(null, 0);
    lastStopAt = 0;
    rechecksLeft = STILL_WRITING_RECHECKS;
    emit();
}
